import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './TemplateEditor.css';

export default function TemplateEditor({ apiBase, onCancel }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [parseMode, setParseMode] = useState('HTML');
  const [mediaType, setMediaType] = useState('none');
  const [mediaSource, setMediaSource] = useState('');
  const [buttons, setButtons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showButtonEditor, setShowButtonEditor] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const mediaFileInputRef = useRef(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBase}/templates`, {
        withCredentials: true,
      });
      setTemplates(res.data.templates);
    } catch (err) {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (templateName) => {
    try {
      setSelectedTemplate(templateName);
      setIsNew(false);
      const res = await axios.get(`${apiBase}/templates/${encodeURIComponent(templateName)}`, {
        withCredentials: true,
      });
      const tpl = res.data;
      setName(tpl.name);
      setText(tpl.text);
      setParseMode(tpl.parse_mode || '');
      if (tpl.media) {
        setMediaType(tpl.media.type);
        setMediaSource(tpl.media.source);
      } else {
        setMediaType('none');
        setMediaSource('');
      }
      setButtons(tpl.buttons || []);
    } catch (err) {
      setError('Failed to load template');
    }
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setIsNew(true);
    setName('');
    setText('');
    setParseMode('HTML');
    setMediaType('none');
    setMediaSource('');
    setButtons([]);
  };

  const mediaAccept =
    mediaType === 'photo'
      ? 'image/*'
      : mediaType === 'video'
        ? 'video/*'
        : mediaType === 'animation'
          ? 'image/gif,video/mp4'
          : undefined;

  const handleBrowseMedia = () => {
    if (!mediaFileInputRef.current) return;
    mediaFileInputRef.current.value = '';
    mediaFileInputRef.current.click();
  };

  const handleMediaFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingMedia(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${apiBase}/media/upload`, formData, {
        withCredentials: true,
      });

      setMediaSource(res.data.source || '');
      setSuccess(`✓ Uploaded "${file.name}"`);
    } catch (err) {
      setError(err.response?.data?.error || 'Media upload failed');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }
    if (!text.trim()) {
      setError('Template text is required');
      return;
    }
    if (mediaType !== 'none' && !mediaSource.trim()) {
      setError('Media source is required for media templates');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const templateData = {
        name: name.trim(),
        text: text.trim(),
        parse_mode: parseMode,
      };

      if (mediaType !== 'none') {
        templateData.media = {
          type: mediaType,
          source: mediaSource,
        };
      }

      if (buttons.length > 0) {
        templateData.buttons = buttons;
      }

      const res = await axios.post(`${apiBase}/templates`, templateData, {
        withCredentials: true,
      });

      setSuccess(`✓ Template "${name}" saved`);
      await loadTemplates();
      setSelectedTemplate(name);
      setIsNew(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) {
      setError('Please select a template to delete');
      return;
    }

    if (!window.confirm(`Delete template "${selectedTemplate}"?`)) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      await axios.delete(`${apiBase}/templates/${selectedTemplate}`, {
        withCredentials: true,
      });
      setSuccess(`✓ Template "${selectedTemplate}" deleted`);
      setSelectedTemplate(null);
      setName('');
      setText('');
      await loadTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async () => {
    if (!selectedTemplate) {
      setError('Please select a template to clone');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const res = await axios.post(
        `${apiBase}/templates/${selectedTemplate}/clone`,
        {},
        { withCredentials: true }
      );
      setSuccess(`✓ Cloned to "${res.data.clone_name}"`);
      await loadTemplates();
      handleSelectTemplate(res.data.clone_name);
    } catch (err) {
      setError(err.response?.data?.error || 'Clone failed');
    } finally {
      setSaving(false);
    }
  };

  const charCount = text.length;
  const charLimit = mediaType !== 'none' ? 1024 : 4096;
  const charStatus =
    charCount > charLimit ? 'error' : charCount > charLimit * 0.85 ? 'warn' : 'ok';
  const canSave =
    !saving &&
    !uploadingMedia &&
    !!name.trim() &&
    !!text.trim() &&
    (mediaType === 'none' || !!mediaSource.trim());

  return (
    <div className="template-editor">
      <h2>✎ Template Editor</h2>

      {error && <div className="alert alert-error">❌ {error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="editor-toolbar">
        {loading ? (
          <p>Loading templates...</p>
        ) : (
          <>
            <select
              className="form-control"
              value={selectedTemplate || ''}
              onChange={(e) =>
                e.target.value
                  ? handleSelectTemplate(e.target.value)
                  : handleNewTemplate()
              }
            >
              <option value="">+ New Template</option>
              {templates.map((tpl) => (
                <option key={tpl} value={tpl}>
                  {tpl}
                </option>
              ))}
            </select>
            <button
              className="btn btn-secondary"
              onClick={loadTemplates}
              disabled={loading}
            >
              ⟳ Reload
            </button>
          </>
        )}
      </div>

      <div className="editor-grid">
        {/* Main Editor */}
        <div className="editor-main">
          <div className="form-group">
            <label className="form-label">Template Name</label>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., my_announcement"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Parse Mode</label>
            <div className="radio-group">
              {['HTML', 'Markdown', 'MarkdownV2', ''].map((mode) => (
                <label key={mode} className="radio-item">
                  <input
                    type="radio"
                    value={mode}
                    checked={parseMode === mode}
                    onChange={(e) => setParseMode(e.target.value)}
                  />
                  <span>{mode || '(none)'}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <div className="form-label-row">
              <label className="form-label">Text / Caption</label>
              <span className={`char-count ${charStatus}`}>
                {charCount} / {charLimit}
              </span>
            </div>
            <textarea
              className="form-control"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter your message text..."
              rows={8}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Media</label>
            <div className="radio-group">
              {['none', 'photo', 'video', 'animation'].map((type) => (
                <label key={type} className="radio-item">
                  <input
                    type="radio"
                    value={type}
                    checked={mediaType === type}
                    onChange={(e) => {
                      setMediaType(e.target.value);
                      if (e.target.value === 'none') setMediaSource('');
                    }}
                  />
                  <span>
                    {type === 'none'
                      ? 'No Media'
                      : type.charAt(0).toUpperCase() + type.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {mediaType !== 'none' && (
            <div className="form-group">
              <label className="form-label">Media Source</label>
              <div className="media-input-group">
                <input
                  type="text"
                  className="form-control"
                  value={mediaSource}
                  onChange={(e) => setMediaSource(e.target.value)}
                  placeholder="e.g., file:media/image.jpg or https://..."
                />
                <input
                  ref={mediaFileInputRef}
                  type="file"
                  accept={mediaAccept}
                  style={{ display: 'none' }}
                  onChange={handleMediaFileSelected}
                />
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={handleBrowseMedia}
                  disabled={uploadingMedia}
                >
                  {uploadingMedia ? 'Uploading…' : '📁 Browse'}
                </button>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Buttons</label>
            <button
              className="btn btn-secondary"
              onClick={() => setShowButtonEditor(true)}
              style={{ width: '100%' }}
            >
              ✎ Edit Buttons
            </button>
            <div className="buttons-preview">
              {buttons.length === 0 ? (
                <p>(no buttons)</p>
              ) : (
                buttons.map((row, idx) => (
                  <div key={idx} className="button-preview-row">
                    <strong>Row {idx + 1}:</strong>
                    {row.map((btn, bidx) => (
                      <span key={bidx} className="button-preview-item">
                        [{btn.text}] → {btn.url}
                      </span>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="editor-sidebar">
          <div className="action-buttons">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!canSave}
            >
              💾 Save
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleClone}
              disabled={saving || !selectedTemplate}
            >
              📋 Clone
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving || !selectedTemplate}
            >
              🗑 Delete
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => onCancel?.()}
              disabled={saving || uploadingMedia}
            >
              Cancel
            </button>
          </div>

          {selectedTemplate && (
            <div className="template-info">
              <h4>Current Template</h4>
              <p>{selectedTemplate}</p>
            </div>
          )}
        </div>
      </div>

      {/* Button Editor Modal */}
      {showButtonEditor && (
        <ButtonEditorModal
          buttons={buttons}
          onSave={(newButtons) => {
            setButtons(newButtons);
            setShowButtonEditor(false);
          }}
          onClose={() => setShowButtonEditor(false)}
        />
      )}
    </div>
  );
}

function ButtonEditorModal({ buttons, onSave, onClose }) {
  const [rows, setRows] = useState(buttons.map((row) => [...row]));

  const addRow = () => {
    setRows([...rows, []]);
  };

  const addButtonToRow = (rowIdx) => {
    const newRows = [...rows];
    newRows[rowIdx] = [...newRows[rowIdx], { text: '', url: '' }];
    setRows(newRows);
  };

  const updateButton = (rowIdx, btnIdx, field, value) => {
    const newRows = [...rows];
    newRows[rowIdx][btnIdx][field] = value;
    setRows(newRows);
  };

  const deleteButton = (rowIdx, btnIdx) => {
    const newRows = [...rows];
    newRows[rowIdx].splice(btnIdx, 1);
    setRows(newRows);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Buttons</h3>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="buttons-editor">
          {rows.map((row, rowIdx) => (
            <div key={rowIdx} className="button-row-editor">
              <h4>Row {rowIdx + 1}</h4>
              {row.map((btn, btnIdx) => (
                <div key={btnIdx} className="button-edit-item">
                  <input
                    type="text"
                    placeholder="Button text"
                    value={btn.text}
                    onChange={(e) =>
                      updateButton(rowIdx, btnIdx, 'text', e.target.value)
                    }
                    className="form-control"
                  />
                  <input
                    type="text"
                    placeholder="Button URL"
                    value={btn.url}
                    onChange={(e) =>
                      updateButton(rowIdx, btnIdx, 'url', e.target.value)
                    }
                    className="form-control"
                  />
                  <button
                    className="btn btn-danger"
                    onClick={() => deleteButton(rowIdx, btnIdx)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                className="btn btn-secondary"
                onClick={() => addButtonToRow(rowIdx)}
              >
                + Add Button
              </button>
            </div>
          ))}

          <button className="btn btn-secondary" onClick={addRow}>
            + Add Row
          </button>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-primary"
            onClick={() => onSave(rows.filter((r) => r.length > 0))}
          >
            ✓ Done
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
